import React, {
  useLayoutEffect,
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Rnd } from "react-rnd";
import classNames from "classnames";
import DailyIframe, { DailyCall } from "@daily-co/daily-js";
import MinusIcon from "@rsuite/icons/Minus";
import ExpandOutlineIcon from "@rsuite/icons/ExpandOutline";
import { RootState } from "../../store";
import {
  ConsultDialogKind,
  videoConsultActions,
} from "../../store/videoConsult-slice";
import CollapsedCallBar from "./CollapsedCallBar";
import EndCallConfirmModal from "./EndCallConfirmModal";
import styles from "./FloatingVideoPanel.module.scss";

// Ported from the medic app's FloatingVideoPanel (itam-doktor-v2,
// feat/video-consult-pip) with Redux in place of Context, a `hasJoined`
// phase model (prejoin X closes without confirm: no leave webhook exists
// yet), and instance-aware handling of Daily's promises. See
// VIDEO-CONSULT-PIP-IMPLEMENTATION-PLAN.md, Step 2.

const MARGIN = 16;
const EXPANDED_DEFAULT_SIZE = { width: 380, height: 480 };
const MINIMIZED_SIZE = { width: 240, height: 180 };
const MIN_WIDTH = 240;
const MIN_HEIGHT = 200;
const MAX_WIDTH_PX = 640;
const MAX_HEIGHT_PX = 600;
const TRANSITION_MS = 200;
// Same breakpoint as the consult dialogs' phone layouts.
const NARROW_SCREEN_QUERY = "(max-width: 480px)";

// Touch-friendly resize handle sizes. Applied via `resizeHandleStyles` on
// coarse pointers so the thin axis is thumb-grabbable; corners get both
// axes bumped.
const COARSE_RESIZE_STYLES = {
  top: { height: 14 },
  bottom: { height: 14 },
  left: { width: 14 },
  right: { width: 14 },
  topLeft: { width: 20, height: 20 },
  topRight: { width: 20, height: 20 },
  bottomLeft: { width: 20, height: 20 },
  bottomRight: { width: 20, height: 20 },
} as const;

type Size = { width: number; height: number };
type Pos = { x: number; y: number };

// Size-aware re-clamp: shrinks `size` only when it overflows the viewport,
// then fits `position` inside the remaining room. Shared across initial
// mount, window resize, and restore-from-minimized so all three paths stay
// in sync.
function clampToViewport(
  size: Size,
  position: Pos,
  vw: number,
  vh: number
): { size: Size; position: Pos } {
  const newSize = {
    width: Math.min(size.width, Math.max(MIN_WIDTH, vw - MARGIN * 2)),
    height: Math.min(size.height, Math.max(MIN_HEIGHT, vh - MARGIN * 2)),
  };
  const maxX = Math.max(MARGIN, vw - newSize.width - MARGIN);
  const maxY = Math.max(MARGIN, vh - newSize.height - MARGIN);
  const newPosition = {
    x: Math.min(Math.max(MARGIN, position.x), maxX),
    y: Math.min(Math.max(MARGIN, position.y), maxY),
  };
  return { size: newSize, position: newPosition };
}

// Daily's setShowLocalVideo() throws until the local participant has joined
// (Prebuilt's prejoin screen is not joined yet). The panel's own state never
// depends on it: callers apply size and minimized state first, and the
// self-view toggle is sent only once the meeting state allows it. A minimize
// made before the join is replayed by the joined-meeting handler.
function setSelfViewIfJoined(frame: DailyCall | null, show: boolean): void {
  if (!frame || frame.meetingState() !== "joined-meeting") return;
  frame.setShowLocalVideo(show);
}

// Live media-query match; false where matchMedia is missing (jsdom).
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

function computeInitial(): { size: Size; position: Pos } {
  return clampToViewport(
    EXPANDED_DEFAULT_SIZE,
    { x: MARGIN, y: MARGIN },
    window.innerWidth,
    window.innerHeight
  );
}

interface FloatingVideoPanelProps {
  /**
   * Fires when Daily emits `joined-meeting`, i.e. the pharmacist is actually
   * in the room (not just on the prejoin screen). Read via ref internally
   * so callback identity churn does not rebuild the iframe.
   */
  onPharmacistJoined?: () => void;
}

const FloatingVideoPanel: React.FC<FloatingVideoPanelProps> = ({
  onPharmacistJoined,
}) => {
  const dispatch = useDispatch();
  const activeConsult = useSelector(
    (state: RootState) => state.videoConsult.activeConsult
  );
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<DailyCall | null>(null);

  const initial = useMemo(computeInitial, []);
  const [size, setSize] = useState<Size>(initial.size);
  const [position, setPosition] = useState<Pos>(initial.position);
  const [isMinimized, setIsMinimized] = useState(false);
  // Read by the joined-meeting handler (registered once per frame), which
  // must see the CURRENT minimized state to replay a prejoin minimize.
  const isMinimizedRef = useRef(false);
  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);
  const [isConfirmingEnd, setIsConfirmingEnd] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Call phase: false while the Daily prebuilt UI still shows its prejoin
  // screen, true once `joined-meeting` fired. The two teardown paths differ
  // in backend semantics (the leave webhook only exists after a join, and
  // reject is only allowed before one), so the X button must know the phase.
  // Ref for race-free reads in handlers, state mirror for rendering.
  const hasJoinedRef = useRef(false);
  const [hasJoined, setHasJoined] = useState(false);

  // Live-tracked coarse-pointer state so resize handles re-size when the
  // user plugs/unplugs a mouse on hybrid devices.
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");

  // Phones: while a consult dialog is open the panel collapses to a bar
  // (CollapsedCallBar) instead of covering the dialog. "Prikaži video" lifts
  // the collapse for the dialog open at that moment only; any change of
  // dialog (another one opens, or the last one closes) clears it, adjusted
  // during render so no frame shows the stale state. The panel's own size,
  // position and minimized state are never touched, so closing the last
  // dialog brings back exactly the panel the advisor had.
  const isNarrow = useMediaQuery(NARROW_SCREEN_QUERY);
  const openConsultDialog = useSelector(
    (state: RootState) => state.videoConsult.openConsultDialog
  );
  const [videoShownFor, setVideoShownFor] = useState<ConsultDialogKind | null>(
    null
  );
  if (videoShownFor !== null && videoShownFor !== openConsultDialog) {
    setVideoShownFor(null);
  }
  const isCollapsed =
    isNarrow &&
    openConsultDialog !== null &&
    videoShownFor !== openConsultDialog;
  const handleShowVideo = useCallback(() => {
    setVideoShownFor(openConsultDialog);
  }, [openConsultDialog]);

  // Snapshot taken at minimize; consumed at restore. Survives resize clamps
  // via the shared helper so the restore target never goes off-viewport.
  const lastExpandedRef = useRef<{ size: Size; position: Pos }>(initial);

  // Mirror the callback into a ref so the iframe-lifecycle effect can stay
  // dependent on `activeConsult` alone. Prevents prop-identity churn from
  // tearing the Daily singleton down and recreating it mid-call.
  const onPharmacistJoinedRef = useRef(onPharmacistJoined);
  useEffect(() => {
    onPharmacistJoinedRef.current = onPharmacistJoined;
  }, [onPharmacistJoined]);

  // useLayoutEffect: cleanup runs BEFORE React removes the iframe from the DOM,
  // allowing frame.destroy() → leave() to send a graceful leave message.
  useLayoutEffect(() => {
    const container = iframeContainerRef.current;
    if (!activeConsult || !container) return;

    // Async completions from THIS effect instance must never touch a newer
    // call: `disposed` short-circuits callbacks after cleanup, and every
    // clear dispatch is the id-guarded variant pinned to this consult.
    const consultId = activeConsult.id;
    let disposed = false;

    let frame: DailyCall;
    try {
      // Daily allows only ONE frame instance per page, and the single-mount
      // design (one panel, one effect keyed on activeConsult) guarantees it.
      frame = DailyIframe.createFrame(container, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
        },
      });
    } catch (error) {
      console.error("[FloatingVideoPanel] createFrame failed:", error);
      // Never joined: the discovery poll re-surfaces the alert (retry path).
      dispatch(videoConsultActions.clearActiveConsultIfId(consultId));
      return;
    }

    frameRef.current = frame;

    const handleJoinedMeeting = () => {
      hasJoinedRef.current = true;
      setHasJoined(true);
      // Minimized on the prejoin screen: the self-view could not be hidden
      // then, so hide it now (Daily sets the joined state before this event).
      if (isMinimizedRef.current) {
        setSelfViewIfJoined(frame, false);
      }
      onPharmacistJoinedRef.current?.();
    };

    const handleLeftMeeting = () => {
      dispatch(videoConsultActions.clearActiveConsultIfId(consultId));
    };

    const handleError = (event: unknown) => {
      console.error("[FloatingVideoPanel] Daily error:", event);
      dispatch(videoConsultActions.clearActiveConsultIfId(consultId));
    };

    // Listeners must be attached BEFORE join(): Daily delivers events
    // asynchronously via postMessage, so late-attached handlers could
    // miss `joined-meeting` under fast-path join conditions.
    frame.on("joined-meeting", handleJoinedMeeting);
    frame.on("left-meeting", handleLeftMeeting);
    frame.on("error", handleError);

    // Join the meeting. The prebuilt UI will show the prejoin screen
    // (enable_prejoin_ui is set on the room by the backend).
    // The joinLink includes the auth token as ?t=TOKEN.
    frame.join({ url: activeConsult.consultantInfo.joinLink }).catch((error) => {
      if (disposed) return;
      console.error("[FloatingVideoPanel] join failed:", error);
      // A join failure is a PREJOIN failure (never joined, no leave webhook):
      // the re-surfaced alert is the retry path, not an ended call.
      dispatch(videoConsultActions.clearActiveConsultIfId(consultId));
    });

    return () => {
      disposed = true;
      frame.off("joined-meeting", handleJoinedMeeting);
      frame.off("left-meeting", handleLeftMeeting);
      frame.off("error", handleError);
      // destroy() leaves the meeting internally, triggering the backend's
      // Daily webhook; its rejection must never become unhandled.
      frame.destroy().catch(() => {});
      frameRef.current = null;
    };
    // NOTE: `onPharmacistJoined` is intentionally NOT in the dep array: it
    // is read via `onPharmacistJoinedRef`. Adding it would rebuild the Daily
    // iframe on every render where the parent passes a new callback identity.
  }, [activeConsult, dispatch]);

  // Re-clamp on viewport resize / device rotation. Size-aware: preserves the
  // user's chosen dimensions when they still fit, shrinks only as a fallback.
  useEffect(() => {
    const onResize = () => {
      const clamped = clampToViewport(
        size,
        position,
        window.innerWidth,
        window.innerHeight
      );
      if (
        clamped.size.width !== size.width ||
        clamped.size.height !== size.height
      ) {
        setSize(clamped.size);
      }
      if (
        clamped.position.x !== position.x ||
        clamped.position.y !== position.y
      ) {
        setPosition(clamped.position);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [size, position]);

  const runTransition = useCallback(() => {
    setIsTransitioning(true);
    const handle = setTimeout(() => setIsTransitioning(false), TRANSITION_MS);
    return () => clearTimeout(handle);
  }, []);

  const handleMinimize = useCallback(() => {
    lastExpandedRef.current = { size, position };
    const clamped = clampToViewport(
      MINIMIZED_SIZE,
      position,
      window.innerWidth,
      window.innerHeight
    );
    setSize(clamped.size);
    setPosition(clamped.position);
    setIsMinimized(true);
    setSelfViewIfJoined(frameRef.current, false);
    runTransition();
  }, [size, position, runTransition]);

  const handleRestore = useCallback(() => {
    const clamped = clampToViewport(
      lastExpandedRef.current.size,
      lastExpandedRef.current.position,
      window.innerWidth,
      window.innerHeight
    );
    setSize(clamped.size);
    setPosition(clamped.position);
    setIsMinimized(false);
    setSelfViewIfJoined(frameRef.current, true);
    runTransition();
  }, [runTransition]);

  const handleRequestEnd = useCallback(() => {
    // Prejoin: nothing is lost by closing, so no confirm modal. The consult is
    // still live (joinedAt null in the DB), so the discovery poll brings the
    // alert straight back; re-appearance IS the retry affordance, and Dismiss
    // on the alert remains the one and only reject path. No suppression here.
    if (!hasJoinedRef.current) {
      dispatch(videoConsultActions.clearActiveConsult());
      return;
    }
    setIsConfirmingEnd(true);
  }, [dispatch]);

  const handleCancelEnd = useCallback(() => {
    setIsConfirmingEnd(false);
  }, []);

  const handleConfirmEnd = useCallback(() => {
    setIsConfirmingEnd(false);
    // Destroying the frame (in the useLayoutEffect cleanup) will call leave()
    // internally, which triggers the Daily webhook on the backend.
    dispatch(videoConsultActions.clearActiveConsult());
  }, [dispatch]);

  // Clamp max dims against the current viewport so Rnd can never let the
  // user resize past the edge during free-form drag-resize.
  const viewportMaxWidth = Math.min(
    MAX_WIDTH_PX,
    Math.max(MIN_WIDTH, window.innerWidth - MARGIN * 2)
  );
  const viewportMaxHeight = Math.min(
    MAX_HEIGHT_PX,
    Math.max(MIN_HEIGHT, window.innerHeight - MARGIN * 2)
  );

  if (!activeConsult) return null;

  const callerName = activeConsult.callerInfo?.joinName?.trim() || "Pacijent";

  return (
    <>
      {/* Collapsed: the wrapper (and the Daily iframe in it) stays mounted
          at its real size, only moved off-screen and hidden, because
          unmounting or reloading the frame ends the call. */}
      <div
        className={classNames(styles.panelWrapper, {
          [styles.panelWrapperCollapsed]: isCollapsed,
        })}
        aria-hidden={isCollapsed || undefined}
      >
        <Rnd
          size={size}
          position={position}
          onDragStop={(_e, d) => setPosition({ x: d.x, y: d.y })}
          onResizeStop={(_e, _dir, ref, _delta, pos) => {
            setSize({
              width: ref.offsetWidth,
              height: ref.offsetHeight,
            });
            setPosition(pos);
          }}
          minWidth={MIN_WIDTH}
          // While minimized the panel targets 240x180; the expanded minimum
          // of 200 would silently clamp it to 200px (react-rnd applies
          // minHeight as a CSS minimum). The medic reference has this bug.
          minHeight={isMinimized ? MINIMIZED_SIZE.height : MIN_HEIGHT}
          maxWidth={viewportMaxWidth}
          maxHeight={viewportMaxHeight}
          enableResizing={!isMinimized}
          resizeHandleStyles={
            !isMinimized && isCoarsePointer ? COARSE_RESIZE_STYLES : undefined
          }
          bounds="parent"
          dragHandleClassName={styles.dragHandle}
          cancel={`.${styles.iconButton}`}
          className={classNames(styles.floatingPanel, {
            [styles.transitioning]: isTransitioning,
          })}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className={styles.dragHandle}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={isMinimized ? handleRestore : handleMinimize}
              title={isMinimized ? "Vrati" : "Minimizuj"}
              aria-label={isMinimized ? "Vrati" : "Minimizuj"}
            >
              {isMinimized ? <ExpandOutlineIcon /> : <MinusIcon />}
            </button>
            <div className={styles.gripDots} aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <button
              type="button"
              className={classNames(styles.iconButton, styles.endCallButton)}
              onClick={handleRequestEnd}
              title={hasJoined ? "Prekini konsultaciju" : "Zatvori"}
              aria-label={hasJoined ? "Prekini konsultaciju" : "Zatvori"}
            >
              &times;
            </button>
          </div>
          <div ref={iframeContainerRef} className={styles.iframeContainer} />
        </Rnd>
      </div>
      {isCollapsed && (
        <CollapsedCallBar
          frameRef={frameRef}
          callerName={callerName}
          onShowVideo={handleShowVideo}
        />
      )}
      <EndCallConfirmModal
        isOpen={isConfirmingEnd}
        onConfirm={handleConfirmEnd}
        onCancel={handleCancelEnd}
      />
    </>
  );
};

export default FloatingVideoPanel;
